/**
 * Push Notification Service (Capacitor)
 *
 * - Android : token FCM via l'événement Capacitor `registration`.
 * - iOS : token FCM via Firebase Messaging (`FcmToken` natif).
 *   Le problème historique : `Messaging.messaging().token` est appelé avant que
 *   `didRegisterForRemoteNotificationsWithDeviceToken` ait fourni le token APNs à Firebase.
 *   Fix : on attend l'événement Capacitor `registration` (qui confirme la fin de l'enregistrement APNs)
 *   puis on laisse 3s à Firebase pour l'échange APNs→FCM avant d'appeler FcmToken.getToken().
 */

import { Capacitor } from '@capacitor/core';
import {
  PushNotifications,
  Token,
  PushNotificationActionPerformed,
  PushNotificationSchema,
} from '@capacitor/push-notifications';
import { buildApiUrl } from '../config/apiConfig';
import { FcmToken } from '../plugins/fcmTokenPlugin';

let listenersAttached = false;
let apnsRegistrationResolver: (() => void) | null = null;
/** Après login / activation : forcer l’envoi du token FCM au backend même si inchangé (Android). */
let pendingAndroidForceSendToken = false;

export const isPushSupported = (): boolean => Capacitor.isNativePlatform();

const getDeviceType = (): 'ios' | 'android' | 'web' => {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') return 'ios';
  if (platform === 'android') return 'android';
  return 'web';
};

const deactivateTokenOnBackend = async (
  fcmToken: string,
  authToken: string,
): Promise<boolean> => {
  try {
    const response = await fetch(buildApiUrl('/notifications/deactivate-token'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ fcm_token: fcmToken }),
    });

    if (response.ok) {
      console.log('[Push] Token désactivé côté serveur');
      return true;
    }
    console.warn('[Push] Échec désactivation token:', response.status, await response.text());
    return false;
  } catch (error) {
    console.warn('[Push] Erreur réseau lors de la désactivation du token:', error);
    return false;
  }
};

const sendTokenToBackend = async (fcmToken: string): Promise<boolean> => {
  try {
    const authToken = localStorage.getItem('token');
    if (!authToken) {
      console.warn('[Push] Pas de JWT token, impossible d\'enregistrer le push token');
      return false;
    }

    const response = await fetch(buildApiUrl('/notifications/register-push-token'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        device_token: fcmToken,
        device_type: getDeviceType(),
      }),
    });

    if (response.ok) {
      const data = await response.json();
      console.log('[Push] Token enregistré avec succès:', data);
      return true;
    }
    console.error('[Push] Erreur lors de l\'enregistrement du token:', response.status, await response.text());
    return false;
  } catch (error) {
    console.error('[Push] Erreur réseau lors de l\'enregistrement du token:', error);
    return false;
  }
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * iOS : attend la fin de l'enregistrement APNs (événement `registration`),
 * donne 3s à Firebase pour l'échange APNs→FCM, puis récupère le token FCM.
 * Retente jusqu'à 10 fois × 1s = 10s.
 */
async function fetchIosFcmTokenAndRegister(forceRegister = false): Promise<boolean> {
  // Attendre que l'événement `registration` Capacitor confirme la fin APNs
  await waitForApnsRegistration();
  // Laisser Firebase compléter l'échange APNs → FCM
  await sleep(3000);

  const jwt = localStorage.getItem('token');
  console.log(`[Push] JWT présent: ${!!jwt}, plateforme: ${Capacitor.getPlatform()}`);

  for (let attempt = 0; attempt < 10; attempt++) {
    try {
      console.log(`[Push] getToken tentative ${attempt + 1}/10…`);
      const { token } = await FcmToken.getToken();
      console.log(`[Push] getToken résultat tentative ${attempt + 1}: token=${token ? token.substring(0, 20) + '…' : 'null'}`);
      if (token) {
        const prev = localStorage.getItem('fcm_token');
        if (forceRegister || prev !== token) {
          localStorage.setItem('fcm_token', token);
          const ok = await sendTokenToBackend(token);
          console.log(`[Push] Token FCM enregistré (tentative ${attempt + 1}):`, ok);
          return ok;
        }
        // Token identique : on force quand même l'envoi au backend (token peut être stale en DB)
        const ok = await sendTokenToBackend(token);
        console.log('[Push] Token FCM inchangé mais re-envoyé au backend:', ok);
        return ok;
      }
    } catch (e: any) {
      console.warn(`[Push] FCM getToken tentative ${attempt + 1} erreur:`, e?.message ?? e);
    }
    await sleep(1000);
  }
  console.error('[Push] FCM token introuvable après 10 tentatives');
  return false;
}

/** Attend que l'événement `registration` Capacitor soit émis (max 10s). */
function waitForApnsRegistration(): Promise<void> {
  if (apnsRegistrationResolver === null) {
    // Déjà résolu ou pas encore démarré — on retourne immédiatement
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const original = apnsRegistrationResolver;
    apnsRegistrationResolver = () => {
      original?.();
      resolve();
    };
    // Timeout de sécurité
    setTimeout(resolve, 10000);
  });
}

function attachPushListeners(): void {
  if (listenersAttached) return;
  listenersAttached = true;

  // Marquer qu'on attend l'événement registration
  apnsRegistrationResolver = () => { apnsRegistrationResolver = null; };

  PushNotifications.addListener('registration', async (token: Token) => {
    // Résoudre l'attente APNs (iOS et Android)
    if (apnsRegistrationResolver) {
      const resolve = apnsRegistrationResolver;
      apnsRegistrationResolver = null;
      resolve();
    }

    if (Capacitor.getPlatform() === 'android') {
      console.log('[Push] Token FCM (Android):', token.value);
      const previousToken = localStorage.getItem('fcm_token');
      const force = pendingAndroidForceSendToken;
      pendingAndroidForceSendToken = false;
      if (force || previousToken !== token.value) {
        localStorage.setItem('fcm_token', token.value);
        const ok = await sendTokenToBackend(token.value);
        console.log('[Push] Token Android envoyé au backend:', ok);
      }
    }
    // iOS : le token ici est APNs (pas FCM). FCM est géré dans fetchIosFcmTokenAndRegister.
  });

  PushNotifications.addListener('registrationError', (error: { message: string }) => {
    console.error('[Push] Erreur d\'enregistrement APNs:', error);
    if (apnsRegistrationResolver) {
      const resolve = apnsRegistrationResolver;
      apnsRegistrationResolver = null;
      resolve(); // ne pas bloquer indéfiniment
    }
  });

  PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
    console.log('[Push] Notification reçue (foreground):', notification);
    window.dispatchEvent(
      new CustomEvent('native-push-received', { detail: notification }),
    );
  });

  PushNotifications.addListener('pushNotificationActionPerformed', (action: PushNotificationActionPerformed) => {
    console.log('[Push] Action sur notification:', action);
    const data = action.notification.data as Record<string, string | undefined> | undefined;
    if (data?.path) {
      const p = String(data.path);
      window.location.href = p.startsWith('/') ? p : `/${p}`;
      return;
    }
    if (data?.type === 'new_message' && data?.conversation_sid) {
      window.location.href = `/app/messages/${data.conversation_sid}`;
    }
  });
}

/**
 * Au login / restauration session : aucune popup.
 * Si la permission est déjà accordée, enregistre le token.
 */
export async function syncPushTokenIfAlreadyEnabled(): Promise<void> {
  if (!isPushSupported()) return;

  try {
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive !== 'granted') return;

    attachPushListeners();
    if (Capacitor.getPlatform() === 'android') {
      pendingAndroidForceSendToken = true;
    }
    await PushNotifications.register(); // iOS: APNs ; Android: FCM via registration

    if (Capacitor.getPlatform() === 'ios') {
      // forceRegister=true : toujours re-envoyer au backend (token peut être stale en DB)
      fetchIosFcmTokenAndRegister(true).catch((e) =>
        console.error('[Push] Erreur sync token iOS:', e)
      );
    }
  } catch (error) {
    console.error('[Push] syncPushTokenIfAlreadyEnabled:', error);
  }
}

export type PushPermissionResult =
  | { ok: true }
  | { ok: false; reason: 'not_native' | 'denied' | 'not_granted' | 'ios_fcm_failed' | 'android_timeout' };

/**
 * À appeler depuis un bouton (geste utilisateur) : affiche la popup iOS si besoin.
 */
export async function requestPushPermissionAndRegister(): Promise<PushPermissionResult> {
  if (!isPushSupported()) return { ok: false, reason: 'not_native' };

  try {
    attachPushListeners();

    let perm = await PushNotifications.checkPermissions();

    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }

    if (perm.receive === 'denied') {
      return { ok: false, reason: 'denied' };
    }

    if (perm.receive !== 'granted') {
      return { ok: false, reason: 'not_granted' };
    }

    if (Capacitor.getPlatform() === 'android') {
      pendingAndroidForceSendToken = true;
    }
    await PushNotifications.register();

    if (Capacitor.getPlatform() === 'ios') {
      // forceRegister=true : toujours envoyer au backend même si le token est identique en localStorage
      const ok = await fetchIosFcmTokenAndRegister(true);
      return ok ? { ok: true } : { ok: false, reason: 'ios_fcm_failed' };
    }

    // Android
    for (let i = 0; i < 60; i++) {
      await sleep(200);
      if (localStorage.getItem('fcm_token')) {
        return { ok: true };
      }
    }
    return { ok: false, reason: 'android_timeout' };
  } catch (error) {
    console.error('[Push] requestPushPermissionAndRegister:', error);
    return { ok: false, reason: 'not_granted' };
  }
}

/** @deprecated Préférer syncPushTokenIfAlreadyEnabled (login) ou requestPushPermissionAndRegister (bouton). */
export const initPushNotifications = async (): Promise<void> => {
  await syncPushTokenIfAlreadyEnabled();
};

/**
 * Désinscrit le token FCM du pro courant côté serveur.
 * À appeler au logout AVANT de supprimer le JWT (ou avec les valeurs capturées).
 */
export const unregisterPushTokenOnLogout = async (
  fcmToken?: string | null,
  authToken?: string | null,
): Promise<void> => {
  if (!isPushSupported()) return;

  const token = fcmToken ?? localStorage.getItem('fcm_token');
  const jwt = authToken ?? localStorage.getItem('token');
  if (token && jwt) {
    await deactivateTokenOnBackend(token, jwt);
  }
};

export const cleanupPushNotifications = async (): Promise<void> => {
  if (!isPushSupported()) return;

  try {
    await PushNotifications.removeAllListeners();
    listenersAttached = false;
    localStorage.removeItem('fcm_token');
    apnsRegistrationResolver = null;
    console.log('[Push] Listeners supprimés');
  } catch (error) {
    console.error('[Push] Erreur lors du cleanup:', error);
  }
};
